/** Trip slot helpers — going cutoff is 90 minutes before start. */

import { localTodayStr } from './date';

export const WEEKDAY_LABELS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

export const RECURRENCE_OPTIONS = [
  { value: 'daily', label: 'Every day' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'once', label: 'One-time (specific date)' },
];

/** @param {{ trip_date?: string | null, recurrence_type?: string, days_of_week?: number[], day_of_month?: number }} slot */
export function isTripSlotActiveOnDate(slot, dateStr) {
  if (!slot || !dateStr) return false;
  const type = slot.recurrence_type || (slot.trip_date ? 'once' : 'daily');
  const [y, m, d] = dateStr.split('-').map((x) => parseInt(x, 10));
  const date = new Date(y, m - 1, d);
  const dow = date.getDay();

  switch (type) {
    case 'daily':
      return !slot.trip_date || slot.trip_date === dateStr;
    case 'once':
      return slot.trip_date === dateStr;
    case 'weekly':
      return Array.isArray(slot.days_of_week) && slot.days_of_week.includes(dow);
    case 'monthly': {
      const dom = slot.day_of_month;
      if (!dom) return false;
      const lastDay = new Date(y, m, 0).getDate();
      return d === Math.min(dom, lastDay);
    }
    default:
      return !slot.trip_date || slot.trip_date === dateStr;
  }
}

/** @param {{ trip_date?: string | null, recurrence_type?: string, days_of_week?: number[], day_of_month?: number }} slot */
export function tripSlotRecurrenceLabel(slot) {
  const type = slot.recurrence_type || (slot.trip_date ? 'once' : 'daily');
  switch (type) {
    case 'daily':
      return 'Every day';
    case 'once':
      return slot.trip_date ? `${slot.trip_date} only` : 'One-time';
    case 'weekly': {
      const days = (slot.days_of_week || [])
        .slice()
        .sort((a, b) => a - b)
        .map((d) => WEEKDAY_LABELS.find((w) => w.value === d)?.label || d)
        .join(', ');
      return days ? `Weekly · ${days}` : 'Weekly';
    }
    case 'monthly':
      return slot.day_of_month ? `Monthly · day ${slot.day_of_month}` : 'Monthly';
    default:
      return slot.trip_date ? `${slot.trip_date} only` : 'Every day';
  }
}

export function timeToHHMM(t) {
  if (!t) return '';
  return String(t).slice(0, 5);
}

export function parseTimeToDateToday(timeStr, tripDateStr, now = new Date()) {
  if (!timeStr || !tripDateStr) return null;
  const parts = String(timeStr).split(':');
  const hh = parseInt(parts[0], 10);
  const mm = parseInt(parts[1], 10);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  const [y, mo, d] = tripDateStr.split('-').map((x) => parseInt(x, 10));
  const date = new Date(y, mo - 1, d, hh, mm, 0, 0);
  return date;
}

export function formatTimeAmPm(timeStr, tripDateStr = null) {
  const d = parseTimeToDateToday(timeStr, tripDateStr || localTodayStr(), new Date());
  if (!d) return timeToHHMM(timeStr);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export function goingCutoffMs(timeStr, tripDateStr, now = new Date()) {
  const start = parseTimeToDateToday(timeStr, tripDateStr, now);
  if (!start) return null;
  return start.getTime() - 90 * 60 * 1000;
}

export function isGoingOpen(timeStr, tripDateStr, now = new Date()) {
  const cutoff = goingCutoffMs(timeStr, tripDateStr, now);
  if (cutoff == null) return false;
  return now.getTime() <= cutoff;
}

export function goingCutoffLabel(timeStr, tripDateStr) {
  const cutoff = goingCutoffMs(timeStr, tripDateStr);
  if (cutoff == null) return '';
  return new Date(cutoff).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
